import {
    Typography,
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { RetryEvent } from '../services/monitor-api.service';
import { Action, canPerformAction } from '../services/permissions.service';
import { useAuth } from '../contexts/AuthContext';

interface RetryQueueListProps {
    events: RetryEvent[];
    getNextRetryTime: (time: string | null) => string;
    getRetryChipColor: (count: number) => 'default' | 'info' | 'warning' | 'error';
    onRetry: (eventId: number) => void;
    onViewDetails: (event: RetryEvent) => void;
}

const strongStyle = { color: '#777777', fontWeight: 700 };

export function RetryQueueList({
    events,
    getNextRetryTime,
    getRetryChipColor,
    onRetry,
    onViewDetails,
}: RetryQueueListProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { role } = useAuth();
    const canRetry = canPerformAction(role, Action.retryEvents);

    // Mobile: Card layout
    if (isMobile) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {events.map((event) => (
                    <Card
                        key={event.id}
                        sx={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onDoubleClick={() => onViewDetails(event)}
                    >
                        <CardContent sx={{ pb: 2 }}>
                            {/* Header with function name and View Details button */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 1,
                                }}
                            >
                                <Typography
                                    variant="body1"
                                    sx={{ fontFamily: 'monospace', fontWeight: 600, flexGrow: 1 }}
                                >
                                    {event.functionName.replace('handle', '')}
                                </Typography>
                                <Tooltip title="View Details">
                                    <IconButton size="small" color="primary" onClick={() => onViewDetails(event)}>
                                        <OpenInNewIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 1,
                                    mb: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    <strong style={strongStyle}>Retry:</strong>{' '}
                                </Typography>
                                <Chip
                                    label={`${event.retryCount}`}
                                    size="small"
                                    color={getRetryChipColor(event.retryCount)}
                                />
                                <Chip label={getNextRetryTime(event.nextRetryTime)} size="small" variant="outlined" />
                                {canRetry && (
                                    <Tooltip title="Resend">
                                        <IconButton size="small" color="warning" onClick={() => onRetry(event.id)}>
                                            <RefreshIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            {/* Description */}
                            {event.description && (
                                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                    <strong style={strongStyle}>Description:</strong> {event.description}
                                </Typography>
                            )}

                            {/* Failed Step */}
                            {event.failedStep && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" component="span">
                                        <strong style={strongStyle}>Failed Step:</strong> {event.failedStep}
                                    </Typography>
                                </Box>
                            )}

                            {/* Error */}
                            {event.lastError && (
                                <Typography variant="body2" color="text.primary">
                                    <strong style={strongStyle}>Error:</strong> {event.lastError}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </Box>
        );
    }

    // Desktop: Table layout
    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Destination</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Retry Count</TableCell>
                        <TableCell align="center">Next Retry</TableCell>
                        <TableCell>Failed Step</TableCell>
                        <TableCell>Error</TableCell>
                        <TableCell align="center">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {events.map((event) => (
                        <TableRow
                            key={event.id}
                            hover
                            sx={{
                                cursor: 'pointer',
                                userSelect: 'none',
                            }}
                            onDoubleClick={() => onViewDetails(event)}
                        >
                            <TableCell>
                                <Typography variant="body2">{event.id}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {event.functionName.replace('handle', '')}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2">{event.description}</Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Chip
                                    label={event.retryCount}
                                    size="small"
                                    color={getRetryChipColor(event.retryCount)}
                                />
                            </TableCell>
                            <TableCell align="center">
                                <Typography variant="body2" color="text.primary">
                                    {getNextRetryTime(event.nextRetryTime)}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" color="text.primary">
                                    {event.failedStep || '-'}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" color="text.primary">
                                    {event.lastError || '-'}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <Tooltip title="View Details">
                                        <IconButton size="small" color="primary" onClick={() => onViewDetails(event)}>
                                            <OpenInNewIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {canRetry && (
                                        <Tooltip title="Resend">
                                            <IconButton size="small" color="primary" onClick={() => onRetry(event.id)}>
                                                <RefreshIcon fontSize="small" color="warning" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
